import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Mail, Phone, MapPin, Twitter, Linkedin, Github, Globe, Loader2, CheckCircle, Shield, Zap, TrendingUp, Users, Target, Rocket, Award, Calendar } from 'lucide-react';
import { cn } from '@/lib/utils';
import { config } from '@/lib/config';
import type { ContactProps } from '@/types';

// Form data interfaces
interface FormData {
  name: string;
  email: string;
  company: string;
  message: string;
}

interface PartnerFormData {
  companyName: string;
  contactName: string;
  contactTitle: string;
  email: string;
  phone: string;
  partnershipTrack: string;
  primaryUseCases: string;
  regionsServed: string;
  additionalInfo: string;
}

interface InvestorFormData {
  name: string;
  firm: string;
  email: string;
  whyNeurosEdge: string;
  ndaRequired: boolean;
}

/**
 * Contact Section Component
 * Contact form and company information
 */
export function Contact({ contactInfo, className }: ContactProps) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [partnerLoading, setPartnerLoading] = useState(false);
  const [investorLoading, setInvestorLoading] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    message: ''
  });

  const [partnerFormData, setPartnerFormData] = useState<PartnerFormData>({
    companyName: '',
    contactName: '',
    contactTitle: '',
    email: '',
    phone: '',
    partnershipTrack: '',
    primaryUseCases: '',
    regionsServed: '',
    additionalInfo: ''
  });

  const [investorFormData, setInvestorFormData] = useState<InvestorFormData>({
    name: '',
    firm: '',
    email: '',
    whyNeurosEdge: '',
    ndaRequired: false
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      toast({
        title: "Validation Error",
        description: "Name is required",
        variant: "destructive"
      });
      return false;
    }

    if (!formData.email.trim()) {
      toast({
        title: "Validation Error",
        description: "Email is required",
        variant: "destructive"
      });
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: "Validation Error",
        description: "Please provide a valid email address",
        variant: "destructive"
      });
      return false;
    }

    if (!formData.message.trim()) {
      toast({
        title: "Validation Error",
        description: "Message is required",
        variant: "destructive"
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${config.api.baseUrl}${config.api.endpoints.contact}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (result.success) {
        toast({
          title: "Success!",
          description: "✅ Your message has been sent successfully!",
          variant: "default"
        });

        // Reset form
        setFormData({
          name: '',
          email: '',
          company: '',
          message: ''
        });
      } else {
        toast({
          title: "Error",
          description: result.message || "❌ Failed to send message. Please try again later.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Error",
        description: "❌ Failed to send message. Please try again later.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePartnerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!partnerFormData.companyName || !partnerFormData.contactName || !partnerFormData.email || !partnerFormData.partnershipTrack || !partnerFormData.primaryUseCases) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    setPartnerLoading(true);

    try {
      // Transform data to match backend API
      const apiData = {
        companyName: partnerFormData.companyName,
        contactNameTitle: `${partnerFormData.contactName}${partnerFormData.contactTitle ? ` - ${partnerFormData.contactTitle}` : ''}`,
        emailPhone: partnerFormData.email + (partnerFormData.phone ? ` / ${partnerFormData.phone}` : ''),
        partnershipTrack: partnerFormData.partnershipTrack,
        primaryUseCases: partnerFormData.primaryUseCases,
        regionsServed: partnerFormData.regionsServed,
        additionalInfo: partnerFormData.additionalInfo
      };

      const response = await fetch(`${config.api.baseUrl}${config.api.endpoints.partner}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiData),
      });

      const result = await response.json();

      if (result.success) {
        toast({
          title: "Success!",
          description: "✅ Your partner application has been submitted successfully! We'll be in touch soon.",
          variant: "default"
        });

        // Reset form
        setPartnerFormData({
          companyName: '',
          contactName: '',
          contactTitle: '',
          email: '',
          phone: '',
          partnershipTrack: '',
          primaryUseCases: '',
          regionsServed: '',
          additionalInfo: ''
        });
      } else {
        toast({
          title: "Error",
          description: result.message || "❌ Failed to submit application. Please try again later.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error submitting partner form:', error);
      toast({
        title: "Error",
        description: "❌ Failed to submit application. Please try again later.",
        variant: "destructive"
      });
    } finally {
      setPartnerLoading(false);
    }
  };

  const handleInvestorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!investorFormData.name || !investorFormData.firm || !investorFormData.email || !investorFormData.whyNeurosEdge) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    setInvestorLoading(true);

    try {
      const response = await fetch(`${config.api.baseUrl}${config.api.endpoints.investor}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(investorFormData),
      });

      const result = await response.json();

      if (result.success) {
        toast({
          title: "Success!",
          description: "✅ Your investor inquiry has been submitted successfully! We'll send you the deck shortly.",
          variant: "default"
        });

        // Reset form
        setInvestorFormData({
          name: '',
          firm: '',
          email: '',
          whyNeurosEdge: '',
          ndaRequired: false
        });
      } else {
        toast({
          title: "Error",
          description: result.message || "❌ Failed to submit inquiry. Please try again later.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error submitting investor form:', error);
      toast({
        title: "Error",
        description: "❌ Failed to submit inquiry. Please try again later.",
        variant: "destructive"
      });
    } finally {
      setInvestorLoading(false);
    }
  };

  return (
    <section id="contact" className={cn("py-20 bg-gradient-subtle", className)}>
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold">
            Get in Touch
          </h2>
          <p className="text-xl text-muted-foreground">
            Have questions about NeurosEdge? Our team is here to help.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Form */}
          <div className="space-y-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="text-sm font-medium">Name *</label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Your name"
                  className="mt-2"
                  disabled={isLoading}
                  required
                />
              </div>

              <div>
                <label htmlFor="email" className="text-sm font-medium">Email *</label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Your email"
                  className="mt-2"
                  disabled={isLoading}
                  required
                />
              </div>

              <div>
                <label htmlFor="company" className="text-sm font-medium">Company</label>
                <Input
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  placeholder="Your company"
                  className="mt-2"
                  disabled={isLoading}
                />
              </div>

              <div>
                <label htmlFor="message" className="text-sm font-medium">Message *</label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="How can we help you?"
                  className="mt-2"
                  rows={4}
                  disabled={isLoading}
                  required
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send Message'
                )}
              </Button>
            </form>
          </div>
          
          {/* Contact Information */}
          <div className="gradient-card rounded-2xl p-8 shadow-card space-y-8 self-center">
            <h3 className="text-2xl font-bold">Connect With Us</h3>
            
            <div className="space-y-6">
              {/* Email */}
              <div className="flex items-center space-x-4">
                <Mail className="w-6 h-6 text-primary" />
                <div>
                  <p className="font-medium">Email Us</p>
                  <p className="text-muted-foreground">{contactInfo.email}</p>
                </div>
              </div>
              
              {/* Phone */}
              <div className="flex items-center space-x-4">
                <Phone className="w-6 h-6 text-primary" />
                <div>
                  <p className="font-medium">Call Us</p>
                  <p className="text-muted-foreground">{contactInfo.phone}</p>
                </div>
              </div>
              
              {/* Address */}
              <div className="flex items-center space-x-4">
                <MapPin className="w-6 h-6 text-primary" />
                <div>
                  <p className="font-medium">Headquarters</p>
                  <p className="text-muted-foreground">
                    111 N Wabash Ave Suite 1722<br />
                    Chicago, IL 60602
                  </p>
                </div>
              </div>
            </div>
            
            {/* Social Links */}
            {/* <div>
              <p className="font-medium mb-4">Follow Us</p>
              <div className="flex space-x-4">
                <Twitter className="w-6 h-6 text-muted-foreground hover:text-primary cursor-pointer transition-colors" />
                <Linkedin className="w-6 h-6 text-muted-foreground hover:text-primary cursor-pointer transition-colors" />
                <Github className="w-6 h-6 text-muted-foreground hover:text-primary cursor-pointer transition-colors" />
                <Globe className="w-6 h-6 text-muted-foreground hover:text-primary cursor-pointer transition-colors" />
              </div>
            </div> */}
          </div>
        </div>
      </div>
    </section>
  );
}
